/**
 * useSEOAnalyticsSettings - Hook to access SEO/Analytics configuration
 * Reads from platform_settings table (seo_analytics_config key)
 */
import { usePlatformSetting } from '@/hooks/useSettings';

export interface SEOAnalyticsConfig {
  // GTM
  gtmEnabled: boolean;
  gtmId: string;
  gtmDebugMode: boolean;
  
  // GA4
  ga4Enabled: boolean;
  ga4MeasurementId: string;
  ga4DebugMode: boolean;
  
  // Search Console
  gscEnabled: boolean;
  gscVerificationMeta: string;
  
  // Bing
  bingEnabled: boolean;
  bingVerificationMeta: string;
  
  // Facebook Pixel
  fbPixelEnabled: boolean;
  fbPixelId: string;
  
  // LinkedIn
  linkedInEnabled: boolean;
  linkedInPartnerId: string;
  
  // Microsoft Clarity
  clarityEnabled: boolean;
  clarityProjectId: string;
  
  // Schema.org
  schemaOrgName: string;
  schemaOrgLogo: string;
  schemaOrgUrl: string;
  schemaContactEmail: string;
  schemaContactPhone: string;
  
  // Other verifications
  yandexVerification: string;
  pinterestVerification: string;
  
  // Consent
  consentModeEnabled: boolean;
  defaultConsentDeny: boolean;
  
  // IndexNow
  indexNowEnabled: boolean;
  indexNowKey: string;
  indexNowAutoSubmit: boolean;
  
  // RSS Feed
  rssEnabled: boolean;
  rssFeedTitle: string;
  rssFeedDescription: string;
  rssMaxItems: number;
}

const defaultConfig: SEOAnalyticsConfig = {
  // Public-safe fallback: the public app cannot always read platform_settings
  // anonymously, so keep the active GTM container available client-side.
  gtmEnabled: true,
  gtmId: 'GTM-5SNSJT86',
  gtmDebugMode: false,
  ga4Enabled: false,
  ga4MeasurementId: '',
  ga4DebugMode: false,
  gscEnabled: false,
  gscVerificationMeta: '',
  bingEnabled: false,
  bingVerificationMeta: '',
  fbPixelEnabled: false,
  fbPixelId: '',
  linkedInEnabled: false,
  linkedInPartnerId: '',
  // Public-safe fallback: keep Microsoft Clarity active client-side even
  // when the public app cannot read platform_settings anonymously.
  clarityEnabled: true,
  clarityProjectId: 'x5yhfz2ikg',
  schemaOrgName: 'BPO Contact',
  schemaOrgLogo: 'https://bpo-contact.com/favicon.svg',
  schemaOrgUrl: 'https://bpo-contact.com',
  schemaContactEmail: 'contact@bpo-contact.com',
  schemaContactPhone: '+212 5 37 37 37 77',
  yandexVerification: '',
  pinterestVerification: '',
  consentModeEnabled: true,
  // Public-safe fallback: keep baseline analytics measurement enabled
  // while remote settings are loading so first-page hits are not lost.
  defaultConsentDeny: false,
  // Public-safe fallback: keep IndexNow active even when the public app
  // cannot read platform_settings due to anonymous RLS restrictions.
  indexNowEnabled: true,
  indexNowKey: 'bpocontact2026indexnow',
  indexNowAutoSubmit: true,
  rssEnabled: true,
  rssFeedTitle: 'BPO Contact Insights',
  rssFeedDescription: 'Latest articles and insights from BPO Contact',
  rssMaxItems: 20,
};

export function useSEOAnalyticsSettings() {
  const { data, isLoading, error } = usePlatformSetting('seo_analytics_config');

  const remoteValue = (data?.value as Partial<SEOAnalyticsConfig> | null) ?? null;
  const config: SEOAnalyticsConfig = {
    ...defaultConfig,
    ...remoteValue,
    gtmEnabled: typeof remoteValue?.gtmEnabled === 'boolean' ? remoteValue.gtmEnabled : defaultConfig.gtmEnabled,
    gtmId: (remoteValue?.gtmId || defaultConfig.gtmId).trim(),
  };

  return {
    config,
    isLoading,
    error,
    
    // Convenience getters
    gtmId: config.gtmEnabled ? config.gtmId : '',
    ga4Id: config.ga4Enabled ? config.ga4MeasurementId : '',
    isGtmEnabled: config.gtmEnabled && !!config.gtmId,
    isGa4Enabled: config.ga4Enabled && !!config.ga4MeasurementId,
    isClarityEnabled: config.clarityEnabled && !!config.clarityProjectId,
    isIndexNowEnabled: config.indexNowEnabled && !!config.indexNowKey,
    isRssEnabled: config.rssEnabled,
    isConsentModeEnabled: config.consentModeEnabled,
  };
}
